import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface AgentModeContextType {
  isAgentMode: boolean;
  enterAgentMode: () => void;
  exitAgentMode: () => void;
}

const AgentModeContext = createContext<AgentModeContextType>({
  isAgentMode: false,
  enterAgentMode: () => {},
  exitAgentMode: () => {},
});

const STORAGE_KEY = "compaire_agent_mode";

const isOrgAdminRoute = (pathname: string) =>
  pathname.startsWith("/admin") || pathname.includes("/org/");

const readStoredAgentMode = () => {
  if (typeof window === "undefined") return true;

  try {
    const stored = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
    if (stored === "false") return false;
    if (stored === "true") return true;
  } catch {}

  return true;
};

const writeStoredAgentMode = (isAgentMode: boolean) => {
  const value = isAgentMode ? "true" : "false";

  try {
    localStorage.setItem(STORAGE_KEY, value);
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {}
};

export const AgentModeProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [isAgentMode, setIsAgentMode] = useState(() => {
    if (typeof window !== "undefined") {
      const pathname = window.location.pathname;

      if (isOrgAdminRoute(pathname)) {
        writeStoredAgentMode(false);
        return false;
      }

      return readStoredAgentMode();
    }

    return true;
  });

  useEffect(() => {
    if (isOrgAdminRoute(location.pathname)) {
      setIsAgentMode(false);
      writeStoredAgentMode(false);
    }
  }, [location.pathname]);

  const enterAgentMode = useCallback(() => {
    writeStoredAgentMode(true);
    setIsAgentMode(true);
  }, []);

  const exitAgentMode = useCallback(() => {
    writeStoredAgentMode(false);
    setIsAgentMode(false);
  }, []);

  return (
    <AgentModeContext.Provider value={{ isAgentMode, enterAgentMode, exitAgentMode }}>
      {children}
    </AgentModeContext.Provider>
  );
};

export const useAgentMode = () => useContext(AgentModeContext);
